<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, watch, onMounted, useTemplateRef, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import UserService from "@/services/UserService"
import AlertModal from "@/components/modals/AlertModal.vue"
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import EditPhoneNumberModal from '@/components/modals/EditPhoneNumberModal.vue'
import Spinner from "@/components/ui/Spinner.vue"
import _ from 'lodash'
import FormButton from '../../components/Atoms/FormButton.vue'
import FormInput from '../../components/Atoms/FormInput.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconEdit from '../../components/icon/IconEdit.vue'
import IconBin from '../../components/icon/IconBin.vue'
import { track, EVENTS } from '@/lib/tracking'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

//TemplateRefs
const ottFile = useTemplateRef('ottFile')

// Refs
const ottPosterurl = ref(false)
const maxPosterUploadMB = ref(2)
const ottLogourl = ref(false)
const processing = ref(false)
const nameProcessing = ref(false)
const unameProcessing = ref(false)
const user = ref(null)
const ottPosterTemp = ref(null)
const ottLogoTemp = ref(null)
const isOtt = ref(false)
const selectedContact = ref(null)
const form = ref({
  name: null,
  username: null,
})
const formErrors = ref({
  username: null,
})
const featureProcessing = ref({
  ottPoster: false,
  ottLogo: false,
})
const features = ref({
  ottPoster: {
    error: false,
    enabled: false,
    value: null,
    valueType: "string",
  },
  ottLogo: {
    error: false,
    enabled: false,
    value: null,
    valueType: "string",
  }
})

const contacts = ref([])

// Computed
const isOwner = computed(() => store.getters['User/isOwner'])
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const userEmail = computed(() => store.getters['User/userEmail'])
const userloaded = computed(() => store.getters['User/userLoaded'])
const userPermissions = computed(() => store.getters['User/userPermissions'])

const activeTeam = computed(() => {
  return store.state.User.activatedTeam
})

const hasAccessToAccountPage = computed(() => {
  if (!activeTeam.value) return true
  if (userPermissions.value?.role === 'admin') return true
  if (userPermissions.value?.accountView) return true
  return false
})

const posterUrl = computed(() => {
  if (ottPosterTemp.value) {
    return ottPosterTemp.value
  }
  const imageId = ottPosterurl.value
  return imageId
    ? `https://assets.castr.io/ottAppPosters/${imageId}`
    : null
})

const logoUrl = computed(() => {
  if (ottLogoTemp.value) {
    return ottLogoTemp.value
  }
  const imageId = ottLogourl.value
  return imageId ? `https://assets.castr.io/ottAppLogo/${imageId}` : null
})

// Hooks
onMounted(async () => {
  store.dispatch('Ui/setLoadingStatus', true)
  await setUser()
  await setupUserContacts()
})

// Watch
watch(activeTeam, () => {
  setUser()
})

// Methods
const onPhoneNumberUpdated = (newContact) => {
  // TODO: support multiple user contacts
  contacts.value = [newContact]
  notify({ group: 'success', text: 'Phone number updated.' })
}

const onPhoneNumberEditClick = (contact) => {
  selectedContact.value = contact
  showModal('edit-phone-number-modal')
}

const onPhoneNumberRemoveClick = (contact) => {
  selectedContact.value = contact
  showModal('confirm-phone-removal')
}
const removePhoneNumber = async () => {
  try {
    await UserService.deleteUserContact(selectedContact.value._id)
    track(EVENTS.PHONE_NUMBER_REMOVED, {})
    contacts.value = _.filter(contacts.value, (i) => {
      return i._id !== selectedContact.value._id
    })

    notify({
      group: "success",
      text: `The phone number: ${(selectedContact.value?.contact?.phoneNumber) || ''} is completely deleted from your account`
    })

    const updatedUser = _.assign({}, UserService.getUser(), { contact: null })
    localStorage.setItem('user', JSON.stringify(updatedUser))

    selectedContact.value = null
  } catch (err) {
    notify({
      group: "error",
      text: err.message || "remove user contact failed",
    })
  }
}

const setupUserContacts = async () => {
  try {
    // Use Vuex store to prevent duplicate API calls
    await store.dispatch('User/setupUserContacts')
    // Clone to prevent potential Vuex mutation errors
    contacts.value = _.cloneDeep(store.state.User.contacts)
  } catch (err) {
    notify({
      group: "error",
      text: err.message || "get user contacts failed",
    })
  }
}

const setUser = async () => {
  processing.value = true
  // Clone to prevent potential Vuex mutation errors
  user.value = _.cloneDeep(store.state.User)
  form.value.name = user.value.name
  form.value.username = user.value.username
  ottPosterurl.value = store.state.User.ottAppPoster
  features.value.ottPoster.value = store.state.User.ottAppPoster
  ottLogourl.value = store.state.User.ottAppLogo
  features.value.ottLogo.value = store.state.User.ottAppLogo
  try {
    // update vuex state
    await store.dispatch('User/checkOtt', null, { root: true })
    isOtt.value = store.state.User.isOtt
  } catch (e) {
    trialError.value = e
    return
  }
  console.log(userloaded.value)
  if (userloaded.value) store.dispatch('Ui/setLoadingStatus', false)
}

const saveottImage = async (featureProp = "ottPoster") => {
  features.value[featureProp].error = false

  let selector
  switch (featureProp) {
    case 'ottPoster':
      selector = "#embed-ott-poster-input"
      break
    case 'ottLogo':
      selector = "#embed-ott-logo-input"
      break
    default:
      selector = "#embed-ott-poster-input"
      break
  }
  const imageInput = document.querySelector(selector)
  const imageFile = imageInput.files[0]
  if (!imageInput.value || !imageFile) {
    features.value[featureProp].error = "please pick an image file"
    return
  }

  // this.featureProcessing.ottPoster = true;
  featureProcessing.value[featureProp] = true

  // -- upload reuqest --
  const fdata = new FormData()
  fdata.append("file", imageFile)

  try {
    if (featureProp === "ottPoster") {
      const res = await UserService.uploadOttPoster(fdata)
      if (res.success) {
        const poster = `https://assets.castr.io/embedImages/${res.uploadId}`
        ottPosterTemp.value = null
        ottPosterurl.value = res.uploadId
        features.value[featureProp].value = res.uploadId

        notify({
          group: "success",
          text: `OTT poster uploaded successfully.`,
        })
      } else {
        features.value[featureProp].error =
          "Unable to upload the image. Please try again later."
      }
    }
    if (featureProp === "ottLogo") {
      const res = await UserService.uploadOttLogo(fdata)
      if (res.success) {
        const poster = `https://assets.castr.io/embedImages/${res.uploadId}`
        ottLogoTemp.value = null
        ottLogourl.value = res.uploadId
        features.value[featureProp].value = res.uploadId

        notify({
          group: "success",
          text: `OTT Logo uploaded successfully.`,
        })
      } else {
        features.value[featureProp].error =
          "Unable to upload the image. Please try again later."
      }
    }
  } catch (err) {
    features.value[featureProp].error =
      "Unable to upload the image. Please try again later."
    notify({
      group: "error",
      text: err.message || "could not upload OTT Poster",
    })
  }

  featureProcessing.value[featureProp] = false
}

const cancelUpload = async (selector = "#embed-ott-poster-input") => {
  let previewFlag
  if (selector === "#embed-ott-poster-input") {
    previewFlag = ottPosterTemp.value
  }
  if (selector === "#embed-ott-logo-input") {
    previewFlag = ottLogoTemp.value
  }
  ottFile.value.reset()

  if (selector === "#embed-ott-poster-input" && previewFlag) {
    ottPosterTemp.value = null
    features.value.ottPoster.error = false
    document.querySelector(selector).value = null
    return
  }
  if (selector === "#embed-ott-logo-input" && previewFlag) {
    ottLogoTemp.value = null
    features.value.ottLogo.error = false
    document.querySelector(selector).value = null
    return
  }
  removeImage.value(selector)
}

const removeImage = async (selector = "#embed-ott-poster-input") => {
  try {
    if (selector === "#embed-ott-poster-input") {
      const deleteRes = await UserService.deleteOttposter()
      if (deleteRes.success) {
        ottPosterurl.value = false
        features.value.ottPoster.value = null
        ottFile.value.reset()
        notify({
          group: "success",
          text: `OTT poster deleted`,
        })
      }
    }
    if (selector === "#embed-ott-logo-input") {
      const deleteRes = await UserService.deleteOttLogo()
      if (deleteRes.success) {
        ottLogourl.value = false
        features.value.ottLogo.value = null
        ottFile.reset()
        notify({
          group: "success",
          text: `OTT logo deleted`,
        })
      }
    }
  } catch (e) {
    console.log(e)
  }
}

const hitUpload = (selector = "#embed-ott-poster-input") => {
  document.querySelector(selector).click()
}

const onottPreview = (selector = "#embed-ott-poster-input") => {
  const imageInput = document.querySelector(selector)
  const imageFile = imageInput.files[0]
  if (!imageFile) return

  if (imageFile.type.indexOf("image/") === -1) {
    showModal("ott_embed-upload-format")
    imageInput.value = null
    return
  }

  let maxSize = 2
  maxPosterUploadMB.value = maxSize

  maxSize *= 1024 * 1024

  if (imageFile.size > maxSize) {
    showModal("ott_embed-image-size-exceed")
    imageInput.value = null
    return
  }

  imageReader(imageFile, (base64) => {
    if (selector === "#embed-ott-poster-input") {
      ottPosterTemp.value = base64
    }
    if (selector === "#embed-ott-logo-input") {
      ottLogoTemp.value = base64
    }
  })
}

const changeName = async () => {
  nameProcessing.value = true
  try {
    const { name } = form.value
    await UserService.changeName(name)

    track(EVENTS.PROFILE_UPDATED, { field: 'name' })

    notify({ group: "success", text: "Account name saved" })
    user.value.name = name
  } catch (e) {
    form.value.name = user.value.name
    notify({
      group: "error",
      text: "Counld not change account name",
    })
  }

  nameProcessing.value = false
}

const changeUsername = async () => {
  formErrors.value.username = null

  const { username } = form.value
  if (username.length < 5) {
    formErrors.value.username = {
      message: "must have atleast 5 letters",
    }
    return
  }

  unameProcessing.value = true

  try {
    const { username } = form.value
    await UserService.changeUsername(username)
    await store.dispatch('User/loadUser')
    track(EVENTS.PROFILE_UPDATED, { field: 'username' })

    notify({ group: "success", text: "Account username saved" })
    // user.value.username = username

  } catch (e) {
    formErrors.value.username = e
    // this.form.username = this.user.username
    notify({
      group: "error",
      text: "Counld not change account username",
    })
  }

  unameProcessing.value = false
}

function imageReader(file, cb) {
  if (!file) {
    return
  }

  var reader = new FileReader()
  reader.onload = function () {
    if (cb) {
      cb(reader.result)
    }
  }

  reader.readAsDataURL(file)
}
</script>

<template>
  <Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
    spinner-size="15px" />
  <div v-else class="view">
    <div class="flex flex-wrap xl:flex-nowrap gap-4" v-if="hasAccessToAccountPage">
      <div class="basis-full xl:basis-5/12 min-w-0">
        <div class="rounded-xl bg-surface-1 px-4 sm:px-6 py-4 flex flex-col gap-4">
          <h3 class="text-lg m-0">Profile</h3>
          <div>
            <FormInput v-model="form.name" label="Name" placeholder="Full name" :disabled="!(activeTeam == null)" />
            <div v-if="activeTeam == null && form.name && form.name !== user?.name" class="mt-2 flex">
              <FormButton class="ml-auto" size="sm" :is-loading="nameProcessing" :disabled="nameProcessing"
                @click="changeName">Save</FormButton>
            </div>
          </div>
          <FormInput label="Email" placeholder="Email" :disabled="!(activeTeam == null)"
            :modelValue="user?.email" readonly />
        </div>
        <div>
          <div class="flex flex-col gap-2 mt-6" v-if="isOtt">
            <label class="text-sm">Upload screen loading poster for OTT app</label>
            <div class="flex flex-wrap gap-2">
              <img v-if="posterUrl" width="100px" :src="posterUrl" class="max-w-full" />
              <input
                id="embed-ott-poster-input"
                ref="ottFile"
                type="file"
                class="custom-upload_dark-3 ott-file-upload text-dark-6 text-s-m"
                @change="onottPreview('#embed-ott-poster-input')"
                :disabled="!(activeTeam == null)"
              />
              <FormButton class="shrink-0" type="secondary" size="sm" v-if="!ottPosterTemp"
                :disabled="featureProcessing.ottPoster || !(activeTeam == null)"
                @click="hitUpload('#embed-ott-poster-input')">
                {{ features.ottPoster.value ? 'Change' : 'Select Poster' }}
              </FormButton>

              <FormButton class="shrink-0" v-if="ottPosterTemp" :disabled="featureProcessing.ottPoster" type="secondary"
                size="sm" @click="saveottImage('ottPoster')">
                {{ featureProcessing.ottPoster ? "Saving .." : "Save Poster" }}
              </FormButton>

              <FormButton class="shrink-0" v-if="ottPosterTemp || features.ottPoster.value"
                :disabled="featureProcessing.ottPoster || !(activeTeam == null)" type="secondary" size="sm"
                @click="cancelUpload('#embed-ott-poster-input')">
                <span v-if="ottPosterTemp && features.ottPoster.value">Restore</span>
                <span v-else-if="ottPosterTemp">Cancel</span>
                <span v-else>Remove</span>
              </FormButton>
            </div>
            <label class="text-s-s">Upload logo for OTT app</label>
            <div class="flex flex-wrap gap-2">
              <img v-if="logoUrl" width="100px" :src="logoUrl" class="max-w-full" />
              <input
                id="embed-ott-logo-input"
                ref="ottFile"
                type="file"
                class="custom-upload_dark-3 ott-file-upload text-dark-6 text-s-m"
                :disabled="!(activeTeam == null)"
                @change="onottPreview('#embed-ott-logo-input')"
              />
              <FormButton class="shrink-0" type="secondary" size="sm" v-if="!ottLogoTemp"
                :disabled="featureProcessing.ottLogo || !(activeTeam == null)" @click="hitUpload('#embed-ott-logo-input')">
                <span v-if="features.ottLogo.value">Change</span>
                <span v-else>Select Logo</span>
              </FormButton>

              <FormButton class="shrink-0" v-if="ottLogoTemp" :disabled="featureProcessing.ottLogo" type="secondary"
                size="sm" @click="saveottImage('ottLogo')">
                <span>{{
                  featureProcessing.ottLogo ? "Saving .." : "Save Logo"
                }}</span>
              </FormButton>

              <FormButton class="shrink-0" v-if="ottLogoTemp || features.ottLogo.value"
                :disabled="featureProcessing.ottLogo || !(activeTeam == null)" type="secondary" size="sm"
                @click="cancelUpload('#embed-ott-logo-input')">
                <span v-if="ottLogoTemp && features.ottLogo.value">Restore</span>
                <span v-else-if="ottLogoTemp">Cancel</span>
                <span v-else>Remove</span>
              </FormButton>
            </div>
          </div>
        </div>
      </div>
      <div class="basis-full xl:basis-7/12 min-w-0">
        <div class="flex flex-col gap-3 rounded-xl bg-surface-1 px-4 sm:px-6 py-4">
          <h3 class="text-lg m-0">Phone numbers</h3>
          <p v-if="contacts.length === 0" class="text-surface-7 p-2">You don’t have phone numbers yet.</p>
          <ul v-else>
            <li v-for="contact in contacts" :key="contact._id"
              class="flex items-center gap-1 text-surface-9 min-w-0">
              <span class="min-w-0 break-all">{{ contact.contact.phoneNumber }}</span>
              <FormButton v-if="isOwner" isIcon type="link" @click="onPhoneNumberEditClick(contact)" class="ml-auto">
                <IconBase>
                  <IconEdit />
                </IconBase>
              </FormButton>
              <FormButton v-if="isOwner" isIcon type="link" @click="onPhoneNumberRemoveClick(contact)">
                <IconBase>
                  <IconBin />
                </IconBase>
              </FormButton>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="no-access mt-0" v-else>
      <div class="text-center pt-5 mt-5">
        <h4 class="text-dark-9 text-s-xl text-500 mb-1">You don’t have permission to access this.</h4>
        <p class="text-dark-6 text-s-m">Contact the team owner at <a class="text-dark-6 text-s-m text-underline"
            :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
      </div>
    </div>
    <alert-modal modal-id="ott_embed-upload-format"
      message="Only valid image files are allowed. Please pick an image file" />
    <alert-modal :message="'Uploaded image too large. Image must not exceed ' +
      maxPosterUploadMB +
      'MB'
      " modal-id="ott_embed-image-size-exceed" />
    <confirm-modal modal-id="confirm-phone-removal"
      :body="`The phone number: ${(selectedContact && selectedContact.contact && selectedContact.contact.phoneNumber) || ''} will be deleted from all streams it linked to. You can\'t undo this action.`"
      message="Delete this phone number?" ok-text="Delete Number" cancel-text="Cancel"
      @modal-confirm="removePhoneNumber()" />

    <edit-phone-number-modal modal-id="edit-phone-number-modal" :contact="selectedContact"
      @user-contact-updated="onPhoneNumberUpdated" />
  </div>
</template>

<style scoped>

</style>
